import {
  BdcDispatchDc,
  BdcDispatchFooter,
  BdcDispatchLine,
  BdcDispatchNote,
  BdcDispatchRecordItem,
  BdcDispatchRecordRequest,
} from "../../../types/bdc.type";

/**
 * บันทึกใบจ่ายกลับ on the client: the desk's typed-but-unsaved text (the draft) laid over the note
 * the server sent. Checks mirror the server's (BE #901): whole counts 0..max, เวลากลับ HH:MM,
 * เลขไมล์เข้า ≥ ออก and ขาด + เหลือ ≤ เบิก per line, all on the merged values — a stored figure
 * counts the same as a typed one. The PUT carries only the keys whose value changed.
 */

/** BDC_DISPATCH_QTY_MAX / BDC_DISPATCH_MILEAGE_MAX / BDC_DISPATCH_NAME_MAX_LENGTH on the server. */
export const DISPATCH_QTY_MAX = 1_000_000;
export const DISPATCH_MILEAGE_MAX = 9_999_999;
export const DISPATCH_NAME_MAX_LENGTH = 200;

export const NAME_FIELDS = ["driver_name", "issuer_name", "recorder_name"] as const;
export const COUNT_FIELDS = ["trays_out", "trays_back", "cn_pcs", "mileage_out", "mileage_in"] as const;
export const LINE_FIELDS = ["short_qty", "leftover_qty"] as const;

export type NameField = (typeof NAME_FIELDS)[number];
export type CountField = (typeof COUNT_FIELDS)[number];
export type FooterField = keyof BdcDispatchFooter;
export type LineField = (typeof LINE_FIELDS)[number];

export type LineDraft = Partial<Record<LineField, string>>;

export interface RecordDraft {
  footer: Partial<Record<FooterField, string>>;
  lines: Record<string, LineDraft>;
}

export const emptyRecordDraft = (): RecordDraft => ({ footer: {}, lines: {} });

/** The order the paper's foot is filled in, with the screen's labels. */
export const FOOTER_FIELDS: ReadonlyArray<{ key: FooterField; label: string; placeholder?: string }> = [
  { key: "driver_name", label: "ชื่อพนักงานจัดส่ง", placeholder: "ชื่อคนขับ" },
  { key: "issuer_name", label: "ผู้จ่าย", placeholder: "ชื่อ" },
  { key: "recorder_name", label: "เจ้าหน้าที่บันทึก", placeholder: "ชื่อ" },
  { key: "trays_out", label: "ถาดขึ้น", placeholder: "0" },
  { key: "trays_back", label: "ถาดกลับ", placeholder: "0" },
  { key: "cn_pcs", label: "ขนม CN (ชิ้น)", placeholder: "0" },
  { key: "return_time", label: "เวลากลับ", placeholder: "--:--" },
  { key: "mileage_out", label: "เลขไมล์ออก", placeholder: "0" },
  { key: "mileage_in", label: "เลขไมล์เข้า", placeholder: "ต้อง ≥ เลขไมล์ออก" },
];

const isNameField = (field: FooterField): field is NameField => (NAME_FIELDS as readonly string[]).includes(field);

const maxOf = (field: CountField | LineField): number =>
  field === "mileage_out" || field === "mileage_in" ? DISPATCH_MILEAGE_MAX : DISPATCH_QTY_MAX;

// ---- parsing ----

export type Parsed<T> = { ok: true; value: T | null } | { ok: false; error: string };

/** "" → null · "1,234" → 1234 · a whole number 0..max, else an error. */
export const parseCount = (text: string, max: number = DISPATCH_QTY_MAX): Parsed<number> => {
  const cleaned = text.replace(/[,\s]/g, "");
  if (cleaned === "") return { ok: true, value: null };
  if (!/^\d+$/.test(cleaned)) return { ok: false, error: "ใส่จำนวนเต็มตั้งแต่ 0" };
  const value = Number(cleaned);
  if (value > max) return { ok: false, error: `ไม่เกิน ${max.toLocaleString("en-US")}` };
  return { ok: true, value };
};

/** "" → null · "14:30", "9:05", "0905", "14.30" → "HH:MM" · anything else (or 24:00+) an error. */
export const parseTime = (text: string): Parsed<string> => {
  const cleaned = text.trim();
  if (cleaned === "") return { ok: true, value: null };
  const match = cleaned.match(/^(\d{1,2})[:.]?(\d{2})$/);
  const error = "เวลาแบบ 24 ชม. เช่น 14:30";
  if (!match) return { ok: false, error };
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return { ok: false, error };
  return { ok: true, value: `${String(hour).padStart(2, "0")}:${match[2]}` };
};

/** A name as it would be stored: trimmed, blank → null. */
export const parseName = (text: string): Parsed<string> => {
  const trimmed = text.trim();
  if (trimmed.length > DISPATCH_NAME_MAX_LENGTH) {
    return { ok: false, error: `ไม่เกิน ${DISPATCH_NAME_MAX_LENGTH} ตัวอักษร` };
  }
  return { ok: true, value: trimmed === "" ? null : trimmed };
};

const parseFooter = (field: FooterField, text: string): Parsed<string | number> => {
  if (isNameField(field)) return parseName(text);
  if (field === "return_time") return parseTime(text);
  return parseCount(text, maxOf(field));
};

// ---- what an input shows / the value a field takes ----

const textOf = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

export const footerText = (note: BdcDispatchNote, draft: RecordDraft, field: FooterField): string =>
  draft.footer[field] ?? textOf(note[field]);

export const lineText = (line: BdcDispatchLine, draft: RecordDraft, field: LineField): string =>
  draft.lines[line.barcode]?.[field] ?? textOf(line[field]);

/** The draft's value when it parses, else the stored one. */
const footerValue = (note: BdcDispatchNote, draft: RecordDraft, field: FooterField): string | number | null => {
  const text = draft.footer[field];
  if (text === undefined) return note[field];
  const parsed = parseFooter(field, text);
  return parsed.ok ? parsed.value : note[field];
};

export const lineValue = (line: BdcDispatchLine, draft: RecordDraft, field: LineField): number | null => {
  const text = draft.lines[line.barcode]?.[field];
  if (text === undefined) return line[field];
  const parsed = parseCount(text);
  return parsed.ok ? parsed.value : line[field];
};

/** ส่งถึง WB = เบิก − ขาด − เหลือ, from the merged values. */
export const deliveredOf = (line: BdcDispatchLine, draft: RecordDraft): number =>
  line.issued_qty - (lineValue(line, draft, "short_qty") ?? 0) - (lineValue(line, draft, "leftover_qty") ?? 0);

export interface RecordTotals {
  issued_qty: number;
  trays: number;
  pieces: number;
  short_qty: number;
  leftover_qty: number;
  delivered_qty: number;
}

export const previewTotals = (note: BdcDispatchNote, draft: RecordDraft): RecordTotals =>
  note.lines.reduce<RecordTotals>(
    (sum, line) => ({
      issued_qty: sum.issued_qty + line.issued_qty,
      trays: sum.trays + line.trays,
      pieces: sum.pieces + line.pieces,
      short_qty: sum.short_qty + (lineValue(line, draft, "short_qty") ?? 0),
      leftover_qty: sum.leftover_qty + (lineValue(line, draft, "leftover_qty") ?? 0),
      delivered_qty: sum.delivered_qty + deliveredOf(line, draft),
    }),
    { issued_qty: 0, trays: 0, pieces: 0, short_qty: 0, leftover_qty: 0, delivered_qty: 0 },
  );

/** ขาด > 0 on some line, counting what is typed — the CN entry point. */
export const hasShort = (note: BdcDispatchNote, draft: RecordDraft): boolean =>
  note.lines.some((line) => (lineValue(line, draft, "short_qty") ?? 0) > 0);

// ---- validation ----

export interface RecordErrors {
  footer: Partial<Record<FooterField, string>>;
  /** barcode → field → message; `sum` = ขาด + เหลือ over เบิก. */
  lines: Record<string, Partial<Record<LineField | "sum", string>>>;
  count: number;
}

export const SUM_EXCEEDS_ISSUED = "ขาด + เหลือ เกินจำนวนเบิก";
export const MILEAGE_IN_BELOW_OUT = "เลขไมล์เข้าต้องไม่น้อยกว่าเลขไมล์ออก";

export const recordErrors = (note: BdcDispatchNote, draft: RecordDraft): RecordErrors => {
  const errors: RecordErrors = { footer: {}, lines: {}, count: 0 };

  (Object.keys(draft.footer) as FooterField[]).forEach((field) => {
    const parsed = parseFooter(field, draft.footer[field] ?? "");
    if (!parsed.ok) {
      errors.footer[field] = parsed.error;
      errors.count += 1;
    }
  });

  const mileageOut = footerValue(note, draft, "mileage_out") as number | null;
  const mileageIn = footerValue(note, draft, "mileage_in") as number | null;
  if (!errors.footer.mileage_in && mileageIn !== null && mileageOut !== null && mileageIn < mileageOut) {
    errors.footer.mileage_in = MILEAGE_IN_BELOW_OUT;
    errors.count += 1;
  }

  note.lines.forEach((line) => {
    const lineErrors: Partial<Record<LineField | "sum", string>> = {};
    const lineDraft = draft.lines[line.barcode] ?? {};
    LINE_FIELDS.forEach((field) => {
      const text = lineDraft[field];
      if (text === undefined) return;
      const parsed = parseCount(text);
      if (!parsed.ok) {
        lineErrors[field] = parsed.error;
        errors.count += 1;
      }
    });
    if (!lineErrors.short_qty && !lineErrors.leftover_qty) {
      const used = (lineValue(line, draft, "short_qty") ?? 0) + (lineValue(line, draft, "leftover_qty") ?? 0);
      if (used > line.issued_qty) {
        lineErrors.sum = SUM_EXCEEDS_ISSUED;
        errors.count += 1;
      }
    }
    if (Object.keys(lineErrors).length > 0) errors.lines[line.barcode] = lineErrors;
  });

  return errors;
};

// ---- the PUT body ----

/**
 * Only the keys whose merged value differs from the stored one; a cleared field sends null.
 * null when nothing changed. Assumes `recordErrors` found none (an unparsable field is skipped).
 */
export const buildRecordBody = (note: BdcDispatchNote, draft: RecordDraft): BdcDispatchRecordRequest | null => {
  const body: BdcDispatchRecordRequest = {};
  (Object.keys(draft.footer) as FooterField[]).forEach((field) => {
    const parsed = parseFooter(field, draft.footer[field] ?? "");
    if (parsed.ok && parsed.value !== note[field]) {
      (body as Record<string, string | number | null>)[field] = parsed.value;
    }
  });

  const items: BdcDispatchRecordItem[] = [];
  note.lines.forEach((line) => {
    const lineDraft = draft.lines[line.barcode];
    if (!lineDraft) return;
    const item: BdcDispatchRecordItem = { barcode: line.barcode };
    LINE_FIELDS.forEach((field) => {
      const text = lineDraft[field];
      if (text === undefined) return;
      const parsed = parseCount(text);
      if (parsed.ok && parsed.value !== line[field]) item[field] = parsed.value;
    });
    if (Object.keys(item).length > 1) items.push(item);
  });
  if (items.length > 0) body.items = items;

  return Object.keys(body).length > 0 ? body : null;
};

export const withFooterText = (draft: RecordDraft, field: FooterField, text: string): RecordDraft => ({
  ...draft,
  footer: { ...draft.footer, [field]: text },
});

export const withLineText = (draft: RecordDraft, barcode: string, field: LineField, text: string): RecordDraft => ({
  ...draft,
  lines: { ...draft.lines, [barcode]: { ...draft.lines[barcode], [field]: text } },
});

// ---- the day's DC list ----

export const STATUS_LABEL: Record<BdcDispatchDc["status"], string> = {
  not_printed: "ยังไม่พิมพ์",
  printed: "พิมพ์แล้ว",
  recorded: "บันทึกแล้ว",
};

/**
 * What the print call sends for a selection: every DC selected → no dc_codes (the server prints
 * the whole day), else the selected codes in the day's order. null when nothing is selected.
 */
export const printDcCodes = (dcs: BdcDispatchDc[], selected: ReadonlySet<string>): string[] | undefined | null => {
  const codes = dcs.filter((dc) => selected.has(dc.dc_code)).map((dc) => dc.dc_code);
  if (codes.length === 0) return null;
  return codes.length === dcs.length ? undefined : codes;
};

/** `ถาด 205 + 30 ชิ้น` — full trays and the loose pieces, as the paper writes them. */
export const traysText = (trays: number, pieces: number): string =>
  pieces > 0 ? `${trays.toLocaleString("en-US")} + ${pieces.toLocaleString("en-US")}` : trays.toLocaleString("en-US");
